import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  templateById,
  templatesForProvider,
  templateChannelLabel,
} from "@/lib/listing-templates";
import TemplateThumbnail from "./TemplateThumbnail";

/**
 * A single connected store's default-template row: selector + live preview
 * thumbnail + Save + quick Preview. Two eBay stores side by side immediately
 * show they can resolve to different templates.
 */
export default function StoreDefaultRow({
  connectorId,
  name,
  provider,
  providerLabel,
  providerIcon,
  savedId,
  onSave,
}: {
  connectorId: string;
  name: string;
  provider: string | null;
  providerLabel: string;
  providerIcon: string;
  savedId: string | null;
  onSave: (connectorId: string, templateId: string | null) => Promise<void>;
}) {
  const available = templatesForProvider(provider);
  const firstId = available[0]?.id ?? null;
  const [draft, setDraft] = useState<string | null>(savedId ?? firstId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  // Re-sync the draft when the persisted value changes externally.
  useEffect(() => {
    setDraft(savedId ?? firstId);
  }, [savedId, firstId]);

  const draftMeta = templateById(draft);
  const dirty = (draft ?? null) !== (savedId ?? null);

  const save = async () => {
    setBusy(true);
    setError("");
    setSaved(false);
    try {
      await onSave(connectorId, draft);
      setSaved(true);
    } catch (err) {
      setError((err as Error).message || "Could not save the default template.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-slate-100 p-3">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-4">
        {/* Store identity */}
        <div className="flex-1 min-w-0 flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
            <i className={`${providerIcon} text-slate-600`} />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-800 truncate">{name}</p>
            <p className="text-xs text-slate-400">{providerLabel}</p>
          </div>
        </div>

        {/* Live preview thumbnail */}
        <div className="flex items-center gap-2 shrink-0">
          <div className="w-16 h-10 rounded-md border border-slate-200 overflow-hidden bg-slate-50">
            <TemplateThumbnail templateId={draft ?? "clean-commerce"} />
          </div>
          <div className="hidden md:block min-w-0 max-w-[140px]">
            <p className="text-xs font-medium text-slate-700 truncate">{draftMeta?.name ?? "—"}</p>
            <p className="text-[11px] text-slate-400">{draftMeta ? templateChannelLabel(draftMeta.channels) : ""}</p>
          </div>
        </div>

        {/* Selector */}
        <div className="flex items-center gap-2 shrink-0">
          <select
            value={draft ?? ""}
            disabled={busy}
            onChange={(e) => setDraft(e.target.value || null)}
            className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 disabled:opacity-60"
          >
            {available.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          <Link
            to={`/listing-templates/preview/${draft ?? "clean-commerce"}`}
            className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
            title="Preview this template"
          >
            <i className="ri-eye-line text-sm" />
          </Link>
          <button
            type="button"
            onClick={save}
            disabled={busy || !dirty}
            className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:hover:bg-emerald-600 text-white text-sm font-medium rounded-lg whitespace-nowrap"
          >
            {busy ? "Saving…" : saved && !dirty ? "Saved" : "Save"}
          </button>
        </div>
      </div>
      {error && <p className="lg:hidden text-xs text-red-600 -mt-1">{error}</p>}
    </div>
  );
}